import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Loader2, Save, Upload, Trash2, History } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/api';

// UAT 07/18 — shared corporate Company Profile form. Single component consumed
// by the Client portal (ClientCompanyProfile) and the HM Admin/Advisor client
// profile (AdminClientProfile). One source of truth: the `companies` document.
export const COMPANY_PROFILE_FIELDS = [
  { key: 'legal_name', tKey: 'legalName' },
  { key: 'name', tKey: 'commercialName' },
  { key: 'tax_id', tKey: 'taxId' },
  { key: 'address', tKey: 'address' },
  { key: 'country', tKey: 'country' },
  { key: 'city', tKey: 'city' },
  { key: 'phone', tKey: 'phone' },
  { key: 'website', tKey: 'website' },
  { key: 'timezone', tKey: 'timezone' },
];

const ROLE_LABELS = {
  hm_admin: { en: 'CTH Admin', es: 'Admin CTH' },
  hm_advisor: { en: 'CTH Advisor', es: 'Consultor CTH' },
  client_admin: { en: 'Client Admin', es: 'Admin de Cliente' },
};

export function CompanyProfileForm({ company, onSave, readOnly = false }) {
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  const [draft, setDraft] = useState(company || {});
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setDraft(company || {}); }, [company]);

  const audit = company?.profile_field_audit || {};
  const latest = Object.entries(audit)
    .map(([field, a]) => ({ field, ...a }))
    .sort((a, b) => (b.at || '').localeCompare(a.at || ''))[0];
  const roleLabel = (r) => (ROLE_LABELS[r] ? (isES ? ROLE_LABELS[r].es : ROLE_LABELS[r].en) : r);
  const fmtDate = (s) => {
    try { return new Date(s).toLocaleString(isES ? 'es-ES' : 'en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return s; }
  };

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 350 * 1024) {
      toast.error(t('pages.companyProfile.logoTooLarge', { defaultValue: 'Logo must be smaller than 350KB' }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setDraft((d) => ({ ...d, logo_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const handleSave = async () => {
    if (!(draft.name || '').trim()) {
      toast.error(t('pages.companyProfile.nameRequired', { defaultValue: 'The commercial name is required' }));
      return;
    }
    setSaving(true);
    try {
      const body = {};
      COMPANY_PROFILE_FIELDS.forEach(({ key }) => { body[key] = (draft[key] || '').trim() || null; });
      body.about = (draft.about || '').trim() || null;
      body.logo_url = draft.logo_url || null;
      await onSave(body);
      toast.success(t('pages.companyProfile.saved', { defaultValue: 'Company profile saved' }));
    } catch (e) {
      toast.error(t('pages.companyProfile.saveFailed', { defaultValue: 'Could not save the company profile' }),
        { description: formatApiError(e) });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="company-profile-shared">
      <Card className="p-6 shadow-card" data-testid="company-profile-logo-card">
        <div className="flex items-center gap-5">
          <div className="h-20 w-20 rounded-xl border border-border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0">
            {draft.logo_url
              ? <img src={draft.logo_url} alt="logo" className="h-full w-full object-contain" data-testid="company-profile-logo-img" />
              : <Building2 className="h-8 w-8 text-muted-foreground/50" />}
          </div>
          <div className="space-y-2">
            <p className="text-sm font-semibold">{t('pages.companyProfile.logo', { defaultValue: 'Company logo' })}</p>
            <p className="text-xs text-muted-foreground">{t('pages.companyProfile.logoHint', { defaultValue: 'PNG or JPG, max 350KB.' })}</p>
            {!readOnly && (
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogo} data-testid="company-profile-logo-input" />
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => fileRef.current?.click()} data-testid="company-profile-logo-upload">
                  <Upload className="h-3.5 w-3.5" />{t('pages.companyProfile.uploadLogo', { defaultValue: 'Upload' })}
                </Button>
                {draft.logo_url && (
                  <Button variant="ghost" size="sm" className="gap-1.5 text-destructive" onClick={() => setDraft((d) => ({ ...d, logo_url: null }))} data-testid="company-profile-logo-remove">
                    <Trash2 className="h-3.5 w-3.5" />{t('pages.companyProfile.removeLogo', { defaultValue: 'Remove' })}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6 shadow-card" data-testid="company-profile-form">
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight">
              {t('pages.companyProfile.generalInfo', { defaultValue: 'General information' })}
            </h2>
            {latest && (
              <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1.5" data-testid="company-profile-last-updated">
                <History className="h-3.5 w-3.5" />
                {t('pages.companyProfile.lastUpdated', {
                  name: latest.name || '—',
                  role: roleLabel(latest.role),
                  date: fmtDate(latest.at),
                  defaultValue: 'Last updated by {{name}} ({{role}}) — {{date}}',
                })}
              </p>
            )}
          </div>
          {!readOnly && (
            <Button size="sm" onClick={handleSave} disabled={saving}
                    className="bg-gradient-primary hover:opacity-90 gap-1.5"
                    data-testid="company-profile-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {t('common.save', { defaultValue: 'Save' })}
            </Button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {COMPANY_PROFILE_FIELDS.map(({ key, tKey }) => (
            <div key={key}>
              <Label className="text-xs">{t(`pages.companyProfile.fields.${tKey}`)}</Label>
              <Input
                value={draft[key] || ''}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                disabled={readOnly}
                data-testid={`company-profile-${key}`}
              />
            </div>
          ))}
        </div>
        <div className="mt-4">
          <Label className="text-xs">{t('pages.companyProfile.fields.about', { defaultValue: 'About the organization' })}</Label>
          <Textarea
            rows={4}
            value={draft.about || ''}
            onChange={(e) => setDraft((d) => ({ ...d, about: e.target.value }))}
            disabled={readOnly}
            data-testid="company-profile-about"
          />
        </div>
        {readOnly && (
          <p className="text-xs text-muted-foreground mt-4" data-testid="company-profile-readonly-note">
            {t('pages.companyProfile.readOnly', { defaultValue: 'Only the company administrator can edit this information.' })}
          </p>
        )}
      </Card>
    </div>
  );
}

export default CompanyProfileForm;
